import * as React from 'react';
import type { JSX } from 'react';

import { XIcon } from 'lucide-react';
import { Dialog as DialogPrimitive } from 'radix-ui';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

const UsersDialog = (props: React.ComponentProps<typeof Dialog>): JSX.Element => <Dialog {...props} />;
UsersDialog.displayName = 'UsersDialog';

const UsersDialogTrigger = (props: React.ComponentProps<typeof DialogTrigger>): JSX.Element => (
  <DialogTrigger {...props} />
);
UsersDialogTrigger.displayName = 'UsersDialogTrigger';

const UsersDialogPortal = (props: React.ComponentProps<typeof DialogPortal>): JSX.Element => (
  <DialogPortal {...props} />
);
UsersDialogPortal.displayName = 'UsersDialogPortal';

const UsersDialogClose = (props: React.ComponentProps<typeof DialogClose>): JSX.Element => <DialogClose {...props} />;
UsersDialogClose.displayName = 'UsersDialogClose';

const UsersDialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    data-slot="dialog-overlay"
    className={cn(
      'fixed inset-0 isolate z-50 bg-black/10 duration-100 supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
      className
    )}
    {...props}
  />
));
UsersDialogOverlay.displayName = 'UsersDialogOverlay';

const UsersDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    showCloseButton?: boolean;
  }
>(({ className, children, showCloseButton = true, ...props }, ref) => (
  <DialogPortal>
    <UsersDialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      data-slot="dialog-content"
      className={cn(
        'fixed top-1/2 left-1/2 z-50 grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 rounded-xl bg-popover p-4 text-sm text-popover-foreground ring-1 ring-foreground/10 duration-100 outline-none sm:max-w-sm data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close data-slot="dialog-close" asChild>
          <Button variant="ghost" className="absolute top-2 right-2" size="icon-sm">
            <XIcon />
            <span className="sr-only">Close</span>
          </Button>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
));
UsersDialogContent.displayName = 'UsersDialogContent';

const UsersDialogHeader = (props: React.ComponentProps<typeof DialogHeader>): JSX.Element => (
  <DialogHeader {...props} />
);
UsersDialogHeader.displayName = 'UsersDialogHeader';

const UsersDialogFooter = (props: React.ComponentProps<typeof DialogFooter>): JSX.Element => (
  <DialogFooter {...props} />
);
UsersDialogFooter.displayName = 'UsersDialogFooter';

const UsersDialogTitle = (props: React.ComponentProps<typeof DialogTitle>): JSX.Element => <DialogTitle {...props} />;
UsersDialogTitle.displayName = 'UsersDialogTitle';

const UsersDialogDescription = (props: React.ComponentProps<typeof DialogDescription>): JSX.Element => (
  <DialogDescription {...props} />
);
UsersDialogDescription.displayName = 'UsersDialogDescription';

export {
  UsersDialog,
  UsersDialogClose,
  UsersDialogContent,
  UsersDialogDescription,
  UsersDialogFooter,
  UsersDialogHeader,
  UsersDialogOverlay,
  UsersDialogPortal,
  UsersDialogTitle,
  UsersDialogTrigger,
};
